import * as Sentry from "@sentry/nextjs";
import { buildServerErrorMetadata } from "@/lib/observability/request-context";
// Lazy-loaded to avoid pulling node:async_hooks into the client bundle.
let _getObservabilityContext:
  | typeof import("@/lib/observability/observability-context").getObservabilityContext
  | undefined;

if (typeof window === "undefined") {
  import("@/lib/observability/observability-context").then((mod) => {
    _getObservabilityContext = mod.getObservabilityContext;
  });
}
import { sanitizeUnknown } from "@/lib/observability/sanitize";
import type { ErrorReportContext } from "@/lib/observability/types";
import { normalizeError } from "@/lib/observability/normalize-error";
import { getOriginalConsoleError } from "@/lib/observability/console-base";
import { isSentryReportingEnabled } from "@/lib/observability/sentry-env";

function safeTagValue(value: unknown): string {
  if (value == null) return "unknown";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "[object]";
}

const PLACEHOLDER_VALUES = new Set(["unknown", "anonymous"]);

/**
 * Strip undefined values and placeholder defaults from an object for cleaner console output.
 * Sentry gets the full unfiltered data separately.
 */
function stripPlaceholders<T extends object>(obj: T): Partial<T> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && PLACEHOLDER_VALUES.has(value)) continue;
    result[key] = value;
  }
  return result as Partial<T>;
}

/** Fill in route/method/action/feature from AsyncLocalStorage when not explicitly set. */
function resolveContextFields(context: {
  route?: string;
  method?: string;
  action?: string;
  feature?: string;
}) {
  const obsCtx = _getObservabilityContext?.();
  if (!obsCtx) return context;
  return {
    ...context,
    route: context.route ?? obsCtx.route,
    method: context.method ?? obsCtx.method,
    action: context.action ?? obsCtx.action,
    feature: context.feature ?? obsCtx.feature,
  };
}

function getServerLog(
  severity: ErrorReportContext["severity"]
): (...args: unknown[]) => void {
  if (severity === "warning") return console.warn.bind(console);
  if (severity === "info") return console.info.bind(console);
  return getOriginalConsoleError();
}

function getServerLogLabel(severity: ErrorReportContext["severity"]): string {
  if (severity === "warning") return "[server-warning]";
  if (severity === "info") return "[server-info]";
  return "[server-error]";
}

export function reportServerError(
  error: unknown,
  context: ErrorReportContext
): string | undefined {
  // Info-severity events should be breadcrumbs, not standalone Sentry issues.
  // Route to reportServerInfoEvent to avoid captureException (fixes NODE-1YM etc.).
  if (context.severity === "info") {
    const normalizedError = normalizeError(error);
    return reportServerInfoEvent(normalizedError.message, context);
  }

  const normalizedError = normalizeError(error);
  const resolved = resolveContextFields(context);
  const runtime = context.runtime ?? "nextjs-server";
  const handled = context.handled ?? true;
  const expected = context.expected ?? false;
  const metadata = buildServerErrorMetadata(context);
  const safeExtra = sanitizeUnknown(context.extra || {}) as Record<
    string,
    unknown
  >;

  const log = getServerLog(context.severity);
  const label = getServerLogLabel(context.severity);
  log(
    label,
    stripPlaceholders({
      source: context.source,
      feature: resolved.feature,
      action: resolved.action,
      component: context.component,
      runtime,
      handled,
      expected,
      suppressionReason: context.suppressionReason,
      route: resolved.route,
      method: resolved.method,
      statusCode: context.statusCode,
      metadata: stripPlaceholders(metadata),
      error: {
        name: normalizedError.name,
        message: normalizedError.message,
        stack: normalizedError.stack,
      },
      extra: safeExtra,
    })
  );

  if (!isSentryReportingEnabled()) {
    return undefined;
  }

  const safeTags = sanitizeUnknown(context.tags || {}) as Record<
    string,
    unknown
  >;
  let eventId: string | undefined;

  Sentry.withScope((scope) => {
    scope.setLevel(context.severity || "error");

    scope.setTag("build_version", metadata.buildVersion);
    scope.setTag("environment", metadata.environment);
    scope.setTag("dns_host", metadata.dnsHost);
    scope.setTag("execution_side", metadata.executionSide);
    scope.setTag("user_country", metadata.userCountry);
    scope.setTag("user_device", metadata.userDevice);
    scope.setTag("user_browser", metadata.userBrowser);
    scope.setTag("user_os", metadata.userOs);
    scope.setTag("user_platform", metadata.userPlatform);
    scope.setTag("runtime", runtime);
    scope.setTag("handled", handled ? "true" : "false");
    scope.setTag("expected", expected ? "true" : "false");

    if (context.source) scope.setTag("source", context.source);
    if (resolved.feature) scope.setTag("feature", resolved.feature);
    if (resolved.action) scope.setTag("action", resolved.action);
    if (context.component) scope.setTag("component", context.component);
    if (resolved.route) scope.setTag("route", resolved.route);
    if (resolved.method) scope.setTag("method", resolved.method);
    if (context.suppressionReason) {
      scope.setTag("suppression_reason", context.suppressionReason);
    }
    if (typeof context.statusCode === "number") {
      scope.setTag("status_code", String(context.statusCode));
    }

    for (const [key, value] of Object.entries(safeTags)) {
      scope.setTag(key, safeTagValue(value));
    }

    scope.setContext("error_metadata", {
      build_version: metadata.buildVersion,
      environment: metadata.environment,
      dns_host: metadata.dnsHost,
      execution_side: metadata.executionSide,
      error_time_iso: metadata.errorTimeIso,
      user_id: metadata.userId,
      user_email: metadata.userEmail,
      user_ip: metadata.userIp,
      user_browser: metadata.userBrowser,
      user_device: metadata.userDevice,
      user_os: metadata.userOs,
      user_platform: metadata.userPlatform,
      user_country: metadata.userCountry,
    });

    scope.setContext("error_context", {
      source: context.source,
      feature: resolved.feature,
      action: resolved.action,
      component: context.component,
      route: resolved.route,
      method: resolved.method,
      status_code: context.statusCode,
      extra: safeExtra,
    });
    scope.setContext("error_classification", {
      runtime,
      handled,
      expected,
      suppression_reason: context.suppressionReason,
    });

    const userId =
      metadata.userId !== "anonymous" ? metadata.userId : undefined;
    const userEmail =
      metadata.userEmail !== "anonymous" ? metadata.userEmail : undefined;
    const userIp = metadata.userIp !== "unknown" ? metadata.userIp : undefined;

    if (userId || userEmail || userIp) {
      scope.setUser({
        id: userId,
        email: userEmail,
        ip_address: userIp,
      });
    }

    eventId = Sentry.captureException(normalizedError);
  });

  return eventId;
}

export function reportServerInfoEvent(
  message: string,
  context: Omit<ErrorReportContext, "severity">
): string | undefined {
  const resolved = resolveContextFields(context);
  const runtime = context.runtime ?? "nextjs-server";
  const handled = context.handled ?? true;
  const expected = context.expected ?? false;
  const metadata = buildServerErrorMetadata({ ...context, severity: "info" });
  const safeExtra = sanitizeUnknown(context.extra || {}) as Record<
    string,
    unknown
  >;
  const log = getServerLog("info");
  const label = getServerLogLabel("info");
  log(
    label,
    stripPlaceholders({
      source: context.source,
      feature: resolved.feature,
      action: resolved.action,
      component: context.component,
      runtime,
      handled,
      expected,
      suppressionReason: context.suppressionReason,
      route: resolved.route,
      method: resolved.method,
      statusCode: context.statusCode,
      metadata: stripPlaceholders(metadata),
      message,
      extra: safeExtra,
    })
  );

  // Info events are breadcrumbs (context for real errors), NOT standalone
  // Sentry issues. captureMessage was previously used here, which created one
  // Sentry issue per info log (NODE-291, NODE-290, NODE-1DC, NODE-296, etc.).
  if (!isSentryReportingEnabled()) {
    return undefined;
  }

  Sentry.addBreadcrumb({
    category: resolved.feature || context.source || "server-info",
    message,
    level: "info",
    data: {
      action: resolved.action,
      component: context.component,
      runtime,
      route: resolved.route,
      method: resolved.method,
      statusCode: context.statusCode,
      ...safeExtra,
    },
  });

  return undefined;
}
